import type { VariableCatalogue } from '../../data/types';

export interface VariableItem {
  /** What gets inserted. */
  token: string;
  /** Shown in mono; falls back to the token. */
  label: string;
  description: string;
  /** Right-aligned resolved value, when one is known. */
  preview?: string;
  /** Bare name a transform chain attaches to, absent when transforms do not apply. */
  transformable?: { name: string };
  /** Expanding shows these instead of inserting the parent. */
  children?: VariableItem[];
}

export interface VariableSection {
  id: string;
  /** Which icon and heading to render; a picker type id for the derived tabs. */
  kind: 'variables' | 'fields' | 'reference' | string;
  title: string;
  items: VariableItem[];
}

export interface ReferenceOption {
  key: string;
  scope: string;
  title: string;
  description: string;
  /** Distinguishes a single image from an `imageCollection` entry when picking an Image source. */
  kind?: string;
}

export interface SectionContext {
  catalogue: VariableCatalogue;
  /** Already filtered and ordered by the caller, which knows its own ordering. */
  fields: { tokenName: string; displayName: string; propertyType: string }[];
  reference: ReferenceOption[];
  /** The property being edited, used to preview the reference lookup key. */
  currentFieldName: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * The picker's content, as data. Tabs are derived rather than enumerated: any
 * field whose type appears in `catalogue.expandable` gets its own tab, so
 * registering a new picker type needs no change here.
 */
export function buildSections({
  catalogue,
  fields,
  reference,
  currentFieldName,
  t
}: SectionContext): VariableSection[] {
  const sections: VariableSection[] = [
    {
      id: 'variables',
      kind: 'variables',
      title: t('variables.sections.variables'),
      items: catalogue.variables.map((variable) => ({
        token: variable.token,
        label: variable.token,
        description: t(`variables.tokens.${variable.name}`),
        preview: variable.preview,
        // Strips the outer `{{`/`}}` so the transform chain can be inserted before them again.
        transformable: { name: variable.token.slice(2, -2) }
      }))
    }
  ];

  const expandableTypes = Object.keys(catalogue.expandable);
  const plain = fields.filter((field) => !expandableTypes.includes(field.propertyType));

  if (plain.length > 0) {
    sections.push({
      id: 'fields',
      kind: 'fields',
      title: t('variables.sections.fields'),
      items: plain.map((field) => item(field.tokenName, field.displayName, t('variables.useValueOf', { name: field.displayName })))
    });
  }

  for (const type of expandableTypes) {
    const matching = fields.filter((field) => field.propertyType === type);
    if (matching.length === 0) continue;

    sections.push({
      id: `type:${type}`,
      kind: type,
      title: t(`propertyTypes.${type}`),
      items: matching.map((field) => ({
        ...item(field.tokenName, field.displayName, t('variables.expandToChoose')),
        children: catalogue.expandable[type].map((subField) =>
          item(`${field.tokenName}.${subField}`, `${field.displayName} · ${subField}`, t('variables.useSubField', { field: subField, name: field.displayName }))
        )
      }))
    });
  }

  // Only a variable can build a key: the resolver substitutes a bare `$Name`
  // token inside `{{@ }}`, and anything else there is taken literally.
  const lookups: VariableItem[] =
    currentFieldName && reference.length > 0
      ? [
          {
            token: '',
            label: t('variables.lookupByVariable'),
            description: t('variables.lookupByVariableHint'),
            children: catalogue.variables.map((variable) => {
              // Strips the outer `{{`/`}}` — the key already sits inside one `{{@ }}` pair.
              const bareVariable = variable.token.slice(2, -2);
              return {
                token: `{{@${bareVariable}_$FieldName}}`,
                label: `<${variable.name}>_${currentFieldName}`,
                description: t('variables.lookupWith', { name: variable.name }),
                transformable: { name: `@${bareVariable}_$FieldName` }
              };
            })
          }
        ]
      : [];

  const direct: VariableItem[] = reference.map((entry) => ({
    token: `{{@${entry.key}}}`,
    label: entry.key,
    description: entry.description || entry.title,
    preview: entry.title === entry.key ? undefined : entry.title,
    transformable: { name: `@${entry.key}` }
  }));

  if (lookups.length > 0 || direct.length > 0) {
    sections.push({
      id: 'reference',
      kind: 'reference',
      title: t('variables.sections.reference'),
      items: [...lookups, ...direct]
    });
  }

  return sections;
}

/** Reference keys are inserted bare, not wrapped — the field IS the key. */
export function buildReferenceKeyItems(entries: ReferenceOption[]): VariableItem[] {
  return entries.map((entry) => ({
    token: entry.key,
    label: entry.key,
    description: entry.description || entry.title
  }));
}

/** A bare `{{@Key}}` names one exact entry, knowable at config time; a `$variable` inside means the key only resolves at generation time. */
const LITERAL_REFERENCE = /^\{\{@([^{}>$]+)\}\}$/;

export function parseLiteralReferenceKey(value: string): string | null {
  const match = LITERAL_REFERENCE.exec(value.trim());
  return match ? match[1].trim() : null;
}

function item(tokenName: string, displayName: string, description: string): VariableItem {
  return {
    token: `{{${tokenName}}}`,
    label: `{{${tokenName}}}`,
    description,
    preview: displayName,
    transformable: { name: tokenName }
  };
}
